import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import { FileDrop } from 'react-file-drop';
import { push } from 'connected-react-router';
import axios from 'axios';

import {
  getSearchParamFromUrl,
  hydrateTableRequestFromUrl,
  saveSearchParamToUrl,
  useGlobalPageSize,
} from '../../../../utils/tableUtils';
import bulkUploadActions from './actions';
import { TextInput } from '../../../../components/PageHeader/TextInput';
import Strings from '../../../../Strings';
import { PageHeader } from '../../../../components/PageHeader';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../../components/PageHeader/Button';
import SimpleModal from '../../../../containers/Modal/SimpleModal';
import { PatientTabs } from '..';
import '../../../../modals/BulkAddPatients/BulkAddPatientsModal.scss';

function BulkUpload(props) {
  const urlParams = new URLSearchParams(window.location.search);
  const offsetFromUrl = urlParams.get('offset');
  const searchFromUrl = getSearchParamFromUrl();
  const globalPageSize = useGlobalPageSize();

  const { orgIds, organizationId, refreshTimestamp } = props;
  const { listUploads, getUploadUrl, deleteFile, onNavigate } = props;

  const [files, setFiles] = React.useState(null);
  const [pagination, setPagination] = React.useState({ offset: 0, limit: globalPageSize, totalRecords: 0 });
  const [searchText, setSearchText] = React.useState(searchFromUrl || '');
  const [showFileUploadModal, setShowFileUploadModal] = React.useState(false);
  const [gcsProgress, setGcsProgress] = React.useState(0);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [fileErrors, setFileErrors] = React.useState([]);

  const searchTime = React.useRef(null);

  const pageRequest = React.useRef({
    offset: offsetFromUrl ? parseInt(offsetFromUrl, 10) : 0,
    limit: globalPageSize,
    search: searchFromUrl || '',
    orgId: (orgIds && orgIds.length > 0) ? orgIds : organizationId,
  });

  const load = React.useCallback(() => {
    listUploads(pageRequest.current).then(resp => {
      setFiles(resp.response?.data || []);
      setPagination(resp.response?.pagination);
    });
  }, [listUploads]);

  React.useEffect(() => {
    hydrateTableRequestFromUrl(pageRequest.current, globalPageSize);
  }, [globalPageSize]);

  React.useEffect(() => {
    load();
  }, [load, refreshTimestamp]);

  const refreshData = React.useCallback(() => {
    load();
  }, [load]);

  const onSortingChange = React.useCallback(sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) {
      return;
    }
    pageRequest.current.offset = 0;
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;
    load();
  }, [load]);

  const onOffsetChange = React.useCallback((offset, limit) => {
    if (isNaN(offset)) return;
    pageRequest.current.offset = offset;
    if (limit) {
      pageRequest.current.limit = limit;
    }
    load();
  }, [load]);

  const onPageSizeChange = React.useCallback(limit => {
    pageRequest.current.limit = limit;
    pageRequest.current.offset = 0;
    load();
  }, [load]);

  const onSearchQueryChange = React.useCallback(query => {
    setSearchText(query);
    pageRequest.current.offset = 0;
    pageRequest.current.search = query;
    saveSearchParamToUrl(query);
    clearTimeout(searchTime.current);
    searchTime.current = setTimeout(() => {
      refreshData();
    }, 1000);
  }, [refreshData]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingFile(true);

    // Validate file type
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    const validExtensions = ['.xlsx', '.xls'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
      setFileErrors(['Please upload a valid Excel file (.xlsx or .xls)']);
      setUploadingFile(false);
      return;
    }

    if (!file) return;
    const data = {
      org_uuid: organizationId,
      filename: file.name,
      content_type: file.type || 'application/octet-stream',
    };

    const urlResponse = await getUploadUrl(data);
    const { upload_url } = urlResponse.response;

    const putResponse = await axios.put(upload_url, file, {
      headers: { 'Content-Type': file.type },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setGcsProgress(percentCompleted);
      },
    });
    if (putResponse.status === 200) {
      setShowFileUploadModal(false);
      setGcsProgress(0);
      setUploadingFile(false);
      refreshData();
    } else {
      setFileErrors(['Failed to upload file. Please try again.']);
      setUploadingFile(false);
    }
  };

  const fileInput = React.createRef();

  const getTabRightContent = React.useCallback(() => {
    return (
      <>
        <TextInput placeholder={Strings.search} onChange={e => onSearchQueryChange(e.target.value)} value={searchText} />
        <Button title="Upload File" onClick={() => setShowFileUploadModal(true)} class={HEADER_BUTTON_DARK_BLUE} />
      </>
    );
  }, [searchText, onSearchQueryChange]);

  const columns = React.useMemo(() => [
    columnHelper.accessor('filename', {
      header: 'File Name',
    }),
    columnHelper.accessor('uploaded_at', {
      header: 'Uploaded At',
      cell: info => moment(info.getValue()).format('YYYY-MM-DD HH:mm:ss'),
    }),
    columnHelper.accessor('sheets', {
      header: 'Sheets',
      cell: info => (info.getValue() ? info.getValue().length : 0),
    }),
    columnHelper.accessor('sheets_status', {
      header: 'Sheets Statuses',
      accessorFn: row => row.sheets?.map(s => s.status).join(', '),
    }),
    columnHelper.accessor('status', {
      header: 'Status',
    }),
  ], []);

  const onDeleteFile = React.useCallback((file) => {
    deleteFile(organizationId, file.uuid).then(() => refreshData());
  }, [refreshData]);

  const buttons = React.useMemo(() => [
    {
      icon: 'delete',
      onClick: (_, file) => onDeleteFile(file),
      text: Strings.deleteFile,
    },
  ], []);

  return (
    <>
      <PageHeader left={Strings.capPatient.bulkAddPatients} right={getTabRightContent()} />
      <Table3
        name="bulk-upload-table"
        columns={columns}
        data={files}
        pagination={pagination}
        buttons={buttons}
        onRowClick={file => file.status === 'creating' ? undefined : onNavigate(`${PatientTabs.bulkUpload.path}/${file.uuid}`, file)}
        onSortingChange={onSortingChange}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
      />
      {showFileUploadModal && (
        <SimpleModal
          name="file-upload-modal"
          title="Upload File"
          onCancel={() => setShowFileUploadModal(false)}
          customButtons={(
            <div className="buttons center" key="button-bar">
              <div className="bottom-button-container">
                <button
                  className="brand-white-gray"
                  type="button"
                  onClick={() => setShowFileUploadModal(false)}
                >
                  {Strings.cancel}
                </button>
              </div>
            </div>
          )}
          content={(
            <label
              htmlFor="file"
              className={`upload-documents-drag-area ${fileErrors.length > 0 ? 'error' : ''}`}
              // style={{
              //   '--progress': `${gcsProgress || uploadingFile ? 33 : 0}%`,
              //   backgroundColor:
              //     (fileErrors?.length > 0) ? 'rgba(218, 84, 125, 0.1)' : undefined,
              // }}
            >
              <FileDrop onDrop={handleFileChange}>
                <input type="file" accept=".xlsx,.xls" onChange={handleFileChange} ref={fileInput} id="file" style={{display: 'none'}} />
                <div
                  className="progress-border"
                  onDragEnter={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }}
                  onDragOver={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }}
                >
                  <div className="progress-container">
                    <div className={`progress ${uploadingFile ? 'rotating' : ''}`}>
                      <div className={`upload-icon ${uploadingFile ? 'rotating' : ''}`} />
                    </div>
                  </div>
                </div>
                <p>{Strings.dragFile}</p>
                <button className="brand-blue" onClick={() => fileInput.current.click()}>{Strings.chooseFile}</button>
              </FileDrop>
            </label>
          )}
        />
      )}
    </>
  );
}

BulkUpload.propTypes = {
  refreshTimestamp: PropTypes.number,
  orgIds: PropTypes.array,
  orgs: PropTypes.array,
  organizationId: PropTypes.string,
  listUploads: PropTypes.func,
  getUploadUrl: PropTypes.func,
  deleteFile: PropTypes.func,
  onNavigate: PropTypes.func,
};

const mapStateToProps = state => {
  return {
    orgIds: state.entities?.orgFilter?.orgIds,
    orgs: state.entities?.orgFilter?.orgs,
    organizationId: state.auth?.organization?.masked_id,
  };
};

const mapDispatchToProps = {
  listUploads: bulkUploadActions.listUploads,
  getUploadUrl: bulkUploadActions.getUploadUrl,
  deleteFile: bulkUploadActions.deleteFile,
  onNavigate: (path, state) => push(path, state),
};

export default connect(mapStateToProps, mapDispatchToProps)(BulkUpload);
